import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LinkButton from 'components/layout/buttons/LinkButton';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';

import { IDeletedRow, getDeletedRowName } from './deletedRows';

/**
 * "Restore a deleted calculation row": lists the rows deleted from the active alternative
 * while the calculation window has been open, newest first, each with a restore button.
 */
const RestoreDeletedRows = ({
  deletedRows,
  restore,
}: {
  deletedRows: IDeletedRow[];
  restore: (deletedRow: IDeletedRow) => void;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.calculationWindow]);
  const [open, setOpen] = useState(false);

  if (deletedRows.length === 0) {
    return null;
  }

  const getKindLabel = (deletedRow: IDeletedRow) => {
    if (deletedRow.kind === 'subproduct') {
      return t(`${translations.calculationWindow}:Deleted material row`);
    }

    if (deletedRow.kind === 'installation') {
      return t(`${translations.calculationWindow}:Deleted installation row`);
    }

    return `${t(`${translations.calculationWindow}:Deleted detail row`)} (${
      deletedRow.parentName
    })`;
  };

  return (
    <div className="restore-deleted-rows">
      <PrimaryButton onClick={() => setOpen(!open)} size="sm">
        {`${t(`${translations.calculationWindow}:Restore deleted row`)} (${
          deletedRows.length
        })`}
      </PrimaryButton>
      {open ? (
        <div className="restore-deleted-rows-list">
          {[...deletedRows].reverse().map((deletedRow) => (
            <div className="restore-deleted-row" key={deletedRow.id}>
              <span className="restore-deleted-row-kind">
                {getKindLabel(deletedRow)}
              </span>
              <span className="restore-deleted-row-name">
                {getDeletedRowName(deletedRow) ||
                  t(`${translations.calculationWindow}:Unnamed row`)}
              </span>
              <LinkButton onClick={() => restore(deletedRow)}>
                {t(`${translations.calculationWindow}:Restore`)}
              </LinkButton>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default RestoreDeletedRows;
